import {Formik, FormikConfig} from 'formik'
import {FC, useCallback, useMemo, useState} from 'react'
import {useMutation, useQuery} from 'react-query'
import {Outlet, useLocation, useNavigate, useParams} from 'react-router-dom'

import {AreaSelectArea} from '../../../api/area/types'
import {BookingQueries} from '../../../api/booking'
import {commonServiceBookingDataAdapter, commonServiceBookingPayByTokenAdapter, PayByTokenServiceBookingDraft, removeFormFromBookingData} from '../../../api/booking/helpers'
import {Instructor, ServiceItem} from '../../../api/booking/types'
import {BookingFormData} from '../../../api/forms/types'
import {getEntityDataByIdFromAppearanceSettings} from '../../../api/helpers/data'
import {PromoError} from '../../../api/promo-codes/types'
import {handlePromoPayError} from '../../../components/promo-code-field/handle-promo-pay-error'
import {handleAddonInvalidError} from '../../../helpers/addon-error-message'
import {isContactBlockedError, showBlockedMessage} from '../../../helpers/blocked-member'
import {usePassNotValidHandler} from '../../../helpers/use-pass-not-valid-handler'
import {auth} from '../../../utils/firebase'
import {useLuxon} from '../../../utils/i18n'
import {useModalActions} from '../../../utils/modals'
import {defaultUserValues, IntegratedSignUpFormType} from '../../auth/forms/integrated-sign-up-form'
import {showChargeRetryHintIfDegraded} from '../../payments/helpers/charge-retry-hint'
import {useHandleDraftEntityStatusChange} from '../../payments/hooks/handle-draft-entity-status-change.hook'
import {usePaymentProvider} from '../../payments/hooks/payment-provider-form.provider'
import {useBookingLoader} from '../hooks'
import {BookingService} from '../index'
import {AppearanceEntityGate} from './appearance-entity-gate'
import {RedirectionComponent} from './components/redirection-component'
import {useOpenBookingValidationModal} from './hooks/open-booking-validation-modal.hook'
import {resolveMinParticipants} from './min-participants'
import {BookingStateParticipant} from './types'

export type CreateServiceBookingFormType = IntegratedSignUpFormType &
  {
    date: string
    venueId?: string
    promoCode?: string | null
    promoRejection?: PromoError | null
    specialRequest: string
    bookingType: 'service' | 'space' | 'teacher'
    maxParticipants: number
    minParticipants: number
    isJustTheSpace: boolean
    serviceId: string | null
    coverEquipment?: boolean
    teacherId?: string | null
    teacher: Instructor | null
    duration?: number | string
    hasSpecialRequests: boolean
    service: ServiceItem | null
    area: AreaSelectArea | null
    useForParticipants?: boolean
    entity: 'service' | 'teacher'
    splitPayment? : boolean,
    paymentMethodId: string | null
    userId: string | null | undefined
    participants: BookingStateParticipant[]
    time: null[] | [number | null, number | null]
    paymentType?: 'oneTimePayment' | 'payNow' | 'payOnLocation'
    flowStartsFromService?: boolean
    formData?: BookingFormData
  }

type Params = {
  venueId: string
  serviceId: string
}

const CreateServiceBookingFormInner: FC = () => {
  const luxon = useLuxon()
  const [entityId, setEntityId] = useState<string | null>(null)
  const {isLoading, setIsLoading} = useBookingLoader()
  const {venueId, serviceId} = useParams<Params>()
  const {closeAllModals} = useModalActions()
  // const initialUserId = useMemo(() => auth.currentUser?.uid, [])
  const navigate = useNavigate()
  const {data: priceDataObjects} = useQuery(BookingQueries.calculateBookingPriceQuery({enabled: false, refetchOnWindowFocus: false}))
  const {openPaymentForm} = usePaymentProvider()
  const location = useLocation()
  const state = location.state as Record<string, unknown> | undefined

  if (!venueId || !serviceId) {
    return null
  }

  const serviceData = getEntityDataByIdFromAppearanceSettings(venueId, serviceId) as ServiceItem
  const {mutate: createServiceBooking} = useMutation(BookingService.mutations.createServiceBookingMutation({}))
  const {mutate: createDraftBooking} = useMutation(BookingService.mutations.createServiceBookingDraftMutation({}))
  const {mutate: createServiceBookingPayByToken} = useMutation(
    BookingService.mutations.createServiceBookingPayByTokenMutation({}),
  )
  const {setStartRefetch} = useHandleDraftEntityStatusChange({
    entity: 'booking',
    entityId,
    isLoading,
    setIsLoading,
  })
  const {openBookingValidationModal} = useOpenBookingValidationModal()
  const handlePassNotValid = usePassNotValidHandler()

  const initialValues = useMemo<CreateServiceBookingFormType>(
    () => ({
      venueId,
      area: null,
      teacher: null,
      participants: [],
      entity: 'service',
      minParticipants: resolveMinParticipants(serviceData),
      specialRequest: '',
      time: [null, null],
      service: serviceData,
      isJustTheSpace: false,
      // eslint-disable-next-line
      serviceId: serviceId!,
      splitPayment: false,
      paymentMethodId: null,
      bookingType: 'service',
      hasSpecialRequests: false,
      maxParticipants: Infinity,
      useForParticipants: false,
      formData: undefined,
      date: luxon.now().toFormat('dd/MM/yyyy'),
      ...defaultUserValues,
    }),
    [serviceData, venueId, serviceId],
  ) //eslint-disable-line

  if (state?.service) {
    // @ts-ignore
    initialValues.service = state.service
    // @ts-ignore
    // eslint-disable-next-line
    initialValues.serviceId = state.service.uid
    // @ts-ignore
    // eslint-disable-next-line
    initialValues.isJustTheSpace = state.service.areaSpace || false
  }

  const handleSubmit = useCallback<FormikConfig<CreateServiceBookingFormType>['onSubmit']>(
    (values, formikHelpers) => {
      const {setFieldValue} = formikHelpers
      if (!priceDataObjects) {
        return
      }
      const specialRequestElement = typeof document !== 'undefined' ? (document.getElementById('specialRequest') as HTMLInputElement | HTMLTextAreaElement | null) : null
      const specialRequestFromDom = specialRequestElement?.value ?? ''
      const valuesWithSpecialRequest: CreateServiceBookingFormType = values.hasSpecialRequests
        ? {...values, specialRequest: specialRequestFromDom}
        : {...values, specialRequest: ''}
      const isPayNow = values.paymentType !== 'payOnLocation'
      const createBookingFunction = isPayNow ? createServiceBookingPayByToken : createServiceBooking
      const dataForAdapter = {
        bookingState: {
          ...valuesWithSpecialRequest,
          userId: auth.currentUser?.uid,
        },
        priceDataObjects,
      }
      let adaptedData = null
      let dataForDraft: PayByTokenServiceBookingDraft | null = null
      if (values.paymentType !== 'payOnLocation') {
        adaptedData = commonServiceBookingPayByTokenAdapter(dataForAdapter)
        dataForDraft = adaptedData
      } else {
        adaptedData = commonServiceBookingDataAdapter(dataForAdapter)
      }
      if (values.paymentType === 'oneTimePayment') {
        if (dataForDraft === null) {
          return
        }
        const dataForDraftToUse = dataForDraft as PayByTokenServiceBookingDraft
        const submitDraft = (data: any, isRetry = false) => {
          createDraftBooking(
            data,
            {
              onSuccess: () => {
                setStartRefetch(true)
                setEntityId(dataForDraftToUse.bookingId || null)
                openPaymentForm?.({
                  entity: 'booking',
                  bookingId: dataForDraftToUse.bookingId,
                  totalPrice: dataForDraftToUse.data.splitPayment ? dataForDraftToUse.data.paidAmount : dataForDraftToUse.data.totalPrice,
                  startProcessCallback: () => setIsLoading(true),
                })
              },
              onError: (error: any) => {
                if (error?.response?.status === 400 && (error?.response?.data?.code === 'FORM_INACTIVE' || error?.response?.data?.message === 'Form is inactive or archived') && !isRetry) {
                  submitDraft(removeFormFromBookingData(data), true)
                  return
                }
                if (handleAddonInvalidError(error)) {
                  return
                }
                if (handlePromoPayError(error, setFieldValue, 'calculateBookingPriceQuery')) {
                  return
                }
                if (handlePassNotValid(error, {participants: values.participants, setFieldValue})) {
                  return
                }
                if (isContactBlockedError(error)) {
                  return showBlockedMessage()
                }
                if (error?.response?.status === 400 && error?.response?.data?.type === 'TIME_SLOT_NOT_AVAILABLE') {
                  setFieldValue('time', [null, null])
                  openBookingValidationModal({type: 'service'})
                }
              },
            }
          )
        }
        submitDraft(adaptedData)
      } else {
        setEntityId(null)
        setIsLoading(true)
        const submitBooking = (data: any, isRetry = false) => {
          createBookingFunction(
            data,
            {
              onSuccess: (booking) => {
                closeAllModals()
                setIsLoading(false)
                const uid = booking.uid ?? (booking as { bookingId?: string }).bookingId
                if (venueId && uid) {
                  navigate(`/${venueId}/bookings/${uid}`, {replace: true})
                }
              },
              onError: (error: any) => {
                console.log(error)
                if (error?.response?.status === 400 && (error?.response?.data?.code === 'FORM_INACTIVE' || error?.response?.data?.message === 'Form is inactive or archived') && !isRetry) {
                  submitBooking(removeFormFromBookingData(data), true)
                  return
                }
                setIsLoading(false)
                if (handleAddonInvalidError(error)) {
                  return
                }
                if (handlePromoPayError(error, setFieldValue, 'calculateBookingPriceQuery')) {
                  return
                }
                if (handlePassNotValid(error, {participants: values.participants, setFieldValue})) {
                  return
                }
                if (isContactBlockedError(error)) {
                  return showBlockedMessage()
                }
                if (error.response?.status === 409) {
                  setFieldValue('time', [null, null])
                  return openBookingValidationModal({type: 'service'})
                }
                if (isPayNow) {
                  showChargeRetryHintIfDegraded(venueId)
                }
              },
            },
          )
        }
        submitBooking(adaptedData)
      }
    },
    [
      venueId,
      navigate,
      setIsLoading,
      closeAllModals,
      openPaymentForm,
      priceDataObjects,
      createDraftBooking,
      createServiceBooking,
      auth.currentUser?.uid,
      openBookingValidationModal,
      createServiceBookingPayByToken,
      handlePassNotValid,
    ],
  )
  return (
    <Formik
      onSubmit={handleSubmit}
      validateOnChange={false}
      initialValues={initialValues}
    >
      <RedirectionComponent>
        <Outlet />
      </RedirectionComponent>
    </Formik>
  )
}

export const CreateServiceBookingForm: FC = () => (
  <AppearanceEntityGate entityParams={['serviceId']}>
    <CreateServiceBookingFormInner />
  </AppearanceEntityGate>
)
